import { useMemo } from "react";
import { differenceInMonths, format, parseISO } from "date-fns";
import { TranslationService } from "../../../../../services/TranslationService";
import { formatDateMonthYear } from "../../../../../utils/FormatUtils";
import { AIBillingDateRange } from "../../../../../services/AIBillingService";
import { DatePickerEditor } from "../../../../shared/components/Editors";

export type AIBillingPeriodOption = "current" | "previous" | "custom";

const MAX_RANGE_MONTHS = 6;
const QUERY_DATE_FORMAT = "yyyy-MM-dd";

const labelStyle = { fontSize: "0.75rem", color: "#777" };

const toIsoDate = (date: Date): string => date.toISOString().substring(0, 10);

const toQueryDate = (value: string): string | undefined => {
  if (!value) {
    return undefined;
  }
  const parsed = new Date(value);
  return isNaN(parsed.getTime())
    ? undefined
    : format(parsed, QUERY_DATE_FORMAT);
};

export function exceedsMaxRange(range: AIBillingDateRange): boolean {
  if (!range.from || !range.to) {
    return false;
  }
  return (
    differenceInMonths(parseISO(range.to), parseISO(range.from)) >=
    MAX_RANGE_MONTHS
  );
}

export function getPeriodRange(
  option: AIBillingPeriodOption,
  reference: Date = new Date()
): AIBillingDateRange {
  if (option === "custom") {
    return {};
  }
  const monthOffset = option === "previous" ? -1 : 0;
  const from = new Date(
    reference.getFullYear(),
    reference.getMonth() + monthOffset,
    1
  );
  const to = new Date(
    reference.getFullYear(),
    reference.getMonth() + monthOffset + 1,
    0
  );
  return { from: toIsoDate(from), to: toIsoDate(to) };
}

const AIBillingPeriodSelector = ({
  value,
  onChange,
  customRange,
  onCustomRangeChange,
  note
}: {
  value: AIBillingPeriodOption;
  onChange: (option: AIBillingPeriodOption) => void;
  customRange: AIBillingDateRange;
  onCustomRangeChange: (range: AIBillingDateRange) => void;
  note?: string;
}) => {
  const { translate } = TranslationService;

  const monthLabels = useMemo(() => {
    const now = new Date();
    const previous = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return {
      current: formatDateMonthYear(now),
      previous: formatDateMonthYear(previous)
    };
  }, []);

  return (
    <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start gap-2 mb-3">
      <div className="d-flex flex-column flex-sm-row gap-2 align-items-start">
        <div>
          <label
            className="form-label mb-1"
            style={{ fontSize: "0.75rem", color: "#777" }}
            htmlFor="aiBillingPeriod"
          >
            {translate.Period}
          </label>
          <select
            id="aiBillingPeriod"
            className="form-select"
            style={{ minWidth: 190, fontSize: "0.8125rem", height: 34 }}
            value={value}
            onChange={(e) => onChange(e.target.value as AIBillingPeriodOption)}
          >
            <option value="current">
              {`${monthLabels.current} · ${translate.AIPeriodCurrentMonth}`}
            </option>
            <option value="previous">
              {`${monthLabels.previous} · ${translate.AIPeriodPreviousMonth}`}
            </option>
            <option value="custom">{translate.AIPeriodCustom}</option>
          </select>
        </div>

        {value === "custom" && (
          <div className="d-flex gap-2 align-items-end">
            <div>
              <label className="form-label mb-1" style={labelStyle}>
                {translate.StartDatev2}
              </label>
              <DatePickerEditor
                onChange={(v) =>
                  onCustomRangeChange({ ...customRange, from: toQueryDate(v) })
                }
                defaultValue={customRange.from}
              />
            </div>
            <div>
              <label className="form-label mb-1" style={labelStyle}>
                {translate.EndDate}
              </label>
              <DatePickerEditor
                onChange={(v) =>
                  onCustomRangeChange({ ...customRange, to: toQueryDate(v) })
                }
                defaultValue={customRange.to}
              />
            </div>
          </div>
        )}
      </div>

      {note && (
        <div
          className="d-flex align-items-start gap-2"
          style={{ fontSize: "0.75rem", color: "#888", maxWidth: 420 }}
        >
          <i className="far fa-info-circle mt-1" />
          <span>{note}</span>
        </div>
      )}
    </div>
  );
};

export default AIBillingPeriodSelector;
